<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { getChargemapUrl } from '~~/app/utils/url'
import useUserStore from '~~/app/store/user'
import useCityStore from '~~/app/store/city'
import DownloadApp from '~~/app/components/Networks/DownloadApp.vue'
import OrderPass from '~~/app/components/Networks/OrderPass.vue'
import { ChargemapCtaIcon } from '#cmui/types'
import Map from '~~/app/components/Map/map.vue'
import PoolsList from '~~/app/components/Cities/PoolsList.vue'
import { useBreakpoints } from '@vueuse/core'
import type { LatLngTuple } from 'leaflet'

const config = useRuntimeConfig()
const cdnUrl = config.app.cdnURL
const route = useRoute()
const { t, n } = useI18n()
const breakpoints = useBreakpoints({ tablet: 768 })
const isMobile = breakpoints.isSmallerOrEqual('tablet')

const { hostname, href } = useRequestURL()
const subDomain = hostname?.split('.')[0]

const userStore = useUserStore()
const cityStore = useCityStore()
const { userAlreadyHasAPass } = storeToRefs(userStore)

const pageIndexFromRoute = ref(Number(route.params.pageIndex as string))

const userId = computed<string>(() => useUserSession().user?.value?.id || '')

const initialCenter = computed<LatLngTuple>(
    () =>
        [cityStore.initialCenterLat, cityStore.initialCenterLng] as LatLngTuple
)

const titleKeyPath = computed<string>(() =>
    pageIndexFromRoute.value === 0 ? 'cities.title' : 'cities.title_paginated'
)

const title = computed<string>(() =>
    t(titleKeyPath.value, {
        city: cityStore.name,
        number: pageIndexFromRoute.value
    })
)

const description = computed<string>(() =>
    t('cities.description', {
        city: cityStore.name
    })
)

const faqForJsonLd: any = computed(() => [
    {
        '@type': 'Question',
        name: t('cities.faq.item1.question', {
            city: cityStore.name
        }),
        acceptedAnswer: {
            '@type': 'Answer',
            text:
                t('cities.faq.item1.answer1', {
                    city: cityStore.name,
                    stationsCount: t(
                        'cities.charging_stations',
                        cityStore.stationsTotalCount
                    ),
                    stationsInCity: t('cities.faq.item1.stations_in_city', {
                        city: cityStore.name
                    }),
                    chargemapCard: t('networks.faq.chargemap_map')
                }) +
                t('cities.faq.item1.answer2', {
                    city: cityStore.name,
                    closeCity1: cityStore.cityData?.citiesAround?.length
                        ? cityStore.cityData.citiesAround[1]!.name
                        : cityStore.name,
                    closeCity2: cityStore.cityData?.citiesAround?.length
                        ? cityStore.cityData.citiesAround[2]!.name
                        : cityStore.name,
                    closeCity3: cityStore.cityData?.citiesAround?.length
                        ? cityStore.cityData.citiesAround[3]!.name
                        : cityStore.name
                })
        }
    },
    {
        '@type': 'Question',
        name: t('cities.faq.item2.question', {
            city: cityStore.name
        }),
        acceptedAnswer: {
            '@type': 'Answer',
            text:
                t('cities.faq.item2.answer1', {
                    pools: t('cities.faq.item1.stations_in_city', {
                        city: cityStore.name
                    })
                }) +
                t('cities.faq.item2.answer2') +
                t('cities.faq.item2.answer3') +
                t('cities.faq.item2.answer4', {
                    chargemapMembers: t('cities.faq.chargemap_members')
                })
        }
    },
    {
        '@type': 'Question',
        name: t('cities.faq.item3.question', {
            city: cityStore.name
        }),
        acceptedAnswer: {
            '@type': 'Answer',
            text: t('cities.faq.item3.answer', {
                city: cityStore.name,
                freeStationsCount: t('cities.faq.free_stations_count', {
                    count: n(cityStore.stationsFreeCount)
                }),
                chargemapCard: t('word.the_chargemap_map')
            })
        }
    },
    {
        '@type': 'Question',
        name: t('cities.faq.item4.question', {
            city: cityStore.name
        }),
        acceptedAnswer: {
            '@type': 'Answer',
            text: t('cities.faq.item4.answer', {
                fastStationsCount: t('cities.faq.fast_stations_count', {
                    count: n(cityStore.stationsFastChargeCount)
                }),
                city: cityStore.name,
                chargemapCard: t('word.the_chargemap_map')
            })
        }
    },
    {
        '@type': 'Question',
        name: t('cities.faq.item5.question', {
            city: cityStore.name
        }),
        acceptedAnswer: {
            '@type': 'Answer',
            text:
                t('cities.faq.item5.answer1', {
                    part1: t('cities.faq.item5.answer1_1')
                }) +
                t('cities.faq.item5.answer2', {
                    network1: cityStore.topNetworks[0]?.name || '',
                    network2: cityStore.topNetworks[1]?.name || '',
                    network3: cityStore.topNetworks[2]?.name || ''
                }) +
                t('cities.faq.item5.answer3', {
                    part1: t('cities.faq.item5.answer3_1'),
                    stationsMap: t('cities.faq.city_stations_map', {
                        city: cityStore.name
                    }),
                    pass: t('word.the_chargemap_pass')
                }) +
                t('cities.faq.item5.answer4', {
                    pass: t('word.the_chargemap_pass')
                })
        }
    }
])

const nbOfPoolsToFetch = computed<number>(() => {
    if (isMobile) {
        return userAlreadyHasAPass.value ? 5 : 4
    }

    return userAlreadyHasAPass.value ? 9 : 8
})

const jsonLdGraphArray: any = computed(() => {
    const array: any = [
        {
            '@type': 'Place',
            url: href,
            name: title.value,
            description: description.value,
            address: {
                '@type': 'PostalAddress',
                addressLocality: cityStore.name,
                addressCountry: cityStore.countryCode
            }
        }
    ]

    if (cityStore.pageIndex === 1) {
        array.push({
            '@type': 'FAQPage',
            mainEntity: faqForJsonLd.value
        })
    }

    return array
})

const scrollToMap = () => {
    const mapElement = document.getElementById('networks-map-title')

    if (mapElement) {
        mapElement.scrollIntoView({ behavior: 'smooth' })
    }
}

useSeoMeta({
    title,
    ogTitle: title,
    description,
    ogDescription: description,
    robots: {
        noindex: config.public.NODE_ENV !== 'production' || subDomain === 'map'
    }
})

useAsyncData(
    computed(
        () =>
            `cities-${route.params.citySlugAndCountryCode}-${route.params.pageIndex || 1}`
    ),
    async () => {
        const citySlugAndCountryCode = route.params
            .citySlugAndCountryCode as string

        if (!citySlugAndCountryCode) {
            throw new Error(
                'citySlugAndCountryCode requires city slug and countryCode'
            )
        }

        cityStore.slug = (
            citySlugAndCountryCode.split('-').slice(0, -1).join('-') || ''
        ).toLowerCase()
        cityStore.countryCode = (
            citySlugAndCountryCode.split('-').pop() || ''
        ).toUpperCase()

        cityStore.pageIndex = pageIndexFromRoute.value || 1

        await cityStore.fetchCityData()
        await cityStore.fetchPoolsInCity(
            (cityStore.pageIndex - 1) * nbOfPoolsToFetch.value,
            nbOfPoolsToFetch.value
        )

        if (
            cityStore.pageIndex > 1 &&
            cityStore.totalPools <= nbOfPoolsToFetch.value
        ) {
            return navigateTo(
                getChargemapUrl(
                    `/cities/${cityStore.slug}-${cityStore.countryCode}`
                ),
                {
                    redirectCode: 302,
                    external: true
                }
            )
        }

        await cityStore.fetchCityStatistics()

        return {
            cityStatistics: cityStore.cityStatistics,
            cityData: cityStore.cityData,
            totalPools: cityStore.totalPools
        }
    }
)

useJsonld(() => ({
    '@context': 'https://schema.org',
    '@graph': jsonLdGraphArray.value
}))

watchEffect(() => {
    // Redirect to the main city page if the page index is 1
    if (pageIndexFromRoute.value === 1) {
        return navigateTo(
            getChargemapUrl(
                `/cities/${cityStore.slug}-${cityStore.countryCode}`
            ),
            {
                redirectCode: 301,
                external: true
            }
        )
    }
})
</script>

<template>
    <NuxtLayout name="seo">
        <i18n-t tag="h1" scope="global" :keypath="titleKeyPath">
            <template #city>
                <span>
                    {{ cityStore.name }}
                </span>
            </template>
            <template #number>
                <span>
                    {{ pageIndexFromRoute }}
                </span>
            </template>
        </i18n-t>
        <i18n-t keypath="cities.intro" tag="p" scope="global">
            <template #city>
                {{ cityStore.name }}
            </template>
            <template #stations>
                <span>
                    {{
                        $t(
                            'cities.charging_stations',
                            cityStore.stationsTotalCount
                        )
                    }}
                </span>
            </template>
        </i18n-t>
        <div
            class="flex md:hidden flex-col items-center justify-center gap-6 pt-6"
        >
            <DownloadApp />
            <OrderPass
                v-if="!userAlreadyHasAPass"
                compatible-prefix="not_compatible"
            />
        </div>

        <div
            v-if="!userId || !userAlreadyHasAPass"
            class="hidden md:grid justify-center items-center gap-7 w-full px-11 pt-8"
            :class="{
                'grid-cols-2': !userId && !userAlreadyHasAPass,
                'grid-cols-1': userId || userAlreadyHasAPass
            }"
        >
            <ChargemapCta
                v-if="!userId"
                class="size-full"
                :cta-text="t('join_chargemap.signup')"
                :image-path="`${cdnUrl}/images/ChargemapPassMobile.svg`"
                :icon="ChargemapCtaIcon.IconUserCircle"
                :href="getChargemapUrl('/user/index')"
                icon-is-solid
            >
                <template #text>
                    <i18n-t keypath="cities.cta_signup.text" scope="global">
                        <template #members>
                            <span>
                                {{ t('networks.cta_signup.members') }}
                            </span>
                        </template>
                        <template #city>
                            {{ cityStore.name }}
                        </template>
                    </i18n-t>
                </template>
            </ChargemapCta>
            <ChargemapCta
                v-if="!userId || !userAlreadyHasAPass"
                class="size-full"
                :cta-text="t('word.order_your_pass')"
                :image-path="`${cdnUrl}/images/PassTroncated.svg`"
                :icon="ChargemapCtaIcon.IconCaddie"
                :href="getChargemapUrl('/pass/purchase/step1')"
                icon-is-solid
            >
                <template #text>
                    <i18n-t
                        keypath="networks.cta_charge.not_compatible.1"
                        scope="global"
                    >
                        <template #network>
                            {{ cityStore.name }}
                        </template>
                    </i18n-t>
                    {{ ' ' }}
                    <i18n-t
                        keypath="networks.cta_charge.not_compatible.2"
                        scope="global"
                    >
                        <template #thousandNetworks>
                            <span>
                                {{ t('networks.cta_charge.thousand_networks') }}
                            </span>
                        </template>
                    </i18n-t>
                </template>
            </ChargemapCta>
        </div>

        <i18n-t
            id="networks-map-title"
            keypath="cities.map_title"
            tag="h2"
            scope="global"
        >
            <template #city>
                <span>
                    {{ cityStore.name }}
                </span>
            </template>
        </i18n-t>

        <div class="overflow-hidden px-2 md:px-11">
            <Map
                class="!rounded-10 md:!rounded-20"
                :city="cityStore.slug"
                :country-id="cityStore.countryId"
                :default-center="initialCenter"
                :default-zoom="11"
            />
        </div>

        <CitiesData
            v-if="
                (cityStore.checkinsCount >= 10 ||
                    !!cityStore.stationsTotalCount) &&
                cityStore.pageIndex === 1
            "
        />

        <div v-if="cityStore.pools.length || cityStore.poolsLoading">
            <i18n-t keypath="cities.pools_title" tag="h2" scope="global">
                <template #city>
                    <span>
                        {{ cityStore.name }}
                    </span>
                </template>
            </i18n-t>
            <PoolsList
                :nb-of-pools-to-fetch
                :class="[cityStore.pageIndex > 1 && 'pb-5 md:pb-12']"
            />
        </div>
        <div v-else class="pb-5 md:pb-12" />

        <CitiesFaq
            v-if="cityStore.pageIndex === 1"
            @scrollToMap="scrollToMap"
        />
    </NuxtLayout>
</template>

<style lang="scss" scoped>
h1 {
    @apply text-center py-6 md:pt-11 md:pb-2.5 px-4 lg:px-0 text-3xl md:text-5xl leading-none lg:leading-10;
}

h2 {
    @apply text-center pt-9 pb-5 md:py-12 px-2 lg:px-0 text-2xl md:text-4xl font-bold leading-normal lg:leading-10;
}

span:first-of-type {
    @apply text-sky-500;
}

p {
    @apply text-center text-neutral-900 text-base md:text-xl font-medium font-inter leading-normal lg:leading-8 px-4 lg:px-0;
}
</style>
